'use client'

import React from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { ambiguousNames } from '../lib/amm'
import { formatUsdPrice } from '../lib/format'
import { useAlkanes } from '../lib/store'
import useTwetchFeed from '../lib/use-twetch-feed'
import AssetAdminMenu from './asset-admin-menu'
import AssetBadges, { nameFlags } from './asset-badges'
import { BadgedIcon, Change, McChip } from './asset-row'
import AssetTables from './asset-tables'
import MarketChart from './tv-market-chart'
import TwetchDoor from './twetch-door'
import { StalePricesChip } from './pools-status'
import StatsRail from './stats-rail'
import TradeSheet from './trade-sheet'

/// One asset, end to end: who it is, what it has done, and the trade.
///
/// Below lg the trade is the fomo numpad sheet, opened by the sticky Buy/Sell
/// bar; at lg the audited panel sits inline in the right rail, because a
/// desktop has a keyboard and the room. Both doors lead to the same machinery.
export default function AssetDetail() {
	const asset = useAlkanes((s) => s.asset)
	// The full registry, for the shared-name check — ambiguity is a property of
	// the SET, so it cannot be decided from one row.
	const assets = useAlkanes((s) => s.assets)
	const verifiedIds = useAlkanes((s) => s.verifiedIds)
	const health = useAlkanes((s) => s.health)
	const stats = useAlkanes((s) => s.assetStats)
	const myAddress = useAlkanes((s) => s.address)

	const [sheet, setSheet] = React.useState<'buy' | 'sell' | null>(null)
	const thesisFeed = useTwetchFeed(Boolean(asset?.id), asset?.id ?? null)
	const theses = React.useMemo(
		() =>
			thesisFeed.posts.map((post) => ({
				post,
				user: thesisFeed.users[String(post.userId)]
			})),
		[thesisFeed.posts, thesisFeed.users]
	)

	const ambiguous = React.useMemo(
		() => ambiguousNames(assets.map((a) => ({ id: a.id, name: a.name }))),
		[assets]
	)
	const additionalVerified = React.useMemo(
		() => assets.filter((candidate) => verifiedIds.includes(candidate.id)),
		[assets, verifiedIds]
	)
	const flags = asset ? nameFlags(asset, ambiguous, additionalVerified) : null

	return (
		<section
			aria-label="Asset detail"
			data-testid="alkanes-asset-detail"
			// Bottom clearance = the pinned stack (Buy/Sell bar ~3.75rem over the
			// 1.75rem status strip + safe inset) plus breathing room, so the
			// last table row can scroll clear of the bar at 375px.
			className="flex flex-col gap-3 pb-[calc(6.5rem+env(safe-area-inset-bottom))] lg:pb-4"
		>
			<div className="grid grid-cols-1 items-start gap-3">
				<div className="min-w-0 overflow-hidden rounded-2xl bg-card">
					<div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pb-3 pt-3.5">
						{asset ? (
							<React.Fragment>
								<div className="flex min-w-0 items-center gap-2.5">
									<BadgedIcon
										token={{ id: asset.id, name: asset.name, icon: asset.icon }}
										verified={flags?.verified ?? false}
										size={40}
									/>
									<div className="min-w-0">
										<h2 className="flex items-center gap-1.5 text-lg font-semibold leading-tight">
											<span className="max-w-[180px] truncate sm:max-w-none">
												{asset.name || asset.id}
											</span>
											{flags && !flags.verified && (
												<AssetBadges flags={flags} name={asset.name} size="md" />
											)}
										</h2>
										{flags?.impostorOf ? (
											<span className="block text-xxs tabular-nums text-destructive-ink">
												Not {asset.name} ({flags.impostorOf}) — unverified {asset.id}
											</span>
										) : (
											<span className="mt-0.5 flex items-center gap-1.5">
												<McChip mc={asset.market_cap_usd} />
												<span className="text-xxs tabular-nums text-muted-foreground">
													{asset.id}
												</span>
											</span>
										)}
									</div>
									<AssetAdminMenu assetId={asset.id} />
								</div>
								<div className="ml-auto flex flex-col items-end gap-0.5">
									<span
										data-testid="alkanes-detail-price"
										className="text-2xl font-bold tabular-nums sm:text-3xl"
									>
										{asset.price_usd !== null ? (
											formatUsdPrice(asset.price_usd)
										) : (
											<span className="text-muted-foreground">—</span>
										)}
									</span>
									<Change
										v={stats?.change_24h_pct ?? asset.change_24h_pct}
										className="text-sm font-semibold"
									/>
								</div>
								{health === 'stale' && <StalePricesChip />}
								{/* Where the price came from, but only when that is a
								    weaker claim than "the index carries this asset". */}
								{asset.price_usd !== null && asset.price_source !== 'pizza' && (
									<span
										data-testid="alkanes-price-source"
										className="w-full text-xxs leading-relaxed text-muted-foreground"
									>
										{asset.price_source === 'derived-2hop'
											? 'Price derived through a counterparty token — this asset has no direct frBTC pool.'
											: 'Price derived from this asset’s own pool reserves, not from the all-pools index.'}
									</span>
								)}
							</React.Fragment>
						) : health === 'down' ? (
							// "Select an asset" over a registry that never answered is an
							// instruction nobody can follow. Say what is true.
							<h2 className="text-lg font-semibold text-muted-foreground">Markets unavailable</h2>
						) : (
							// Cold deep-link skeleton with the loaded header's geometry,
							// so the price does not shove the chart down when it lands.
							<React.Fragment>
								<div className="flex min-w-0 items-center gap-2.5">
									<Skeleton className="size-10 shrink-0 rounded-full" />
									<div className="space-y-1.5">
										<Skeleton className="h-[22px] w-28" />
										<Skeleton className="h-3 w-16" />
									</div>
								</div>
								<Skeleton className="ml-auto h-8 w-32" />
							</React.Fragment>
						)}
					</div>

					{/* Not shorter than 300px on a phone: MarketChart carries its own
					    `min-h-[300px]`, and a 280px box let the time axis paint
					    straight through the top row of the stats grid below it. */}
					<div className="h-[320px] border-t border-border sm:h-[380px] lg:h-[440px]">
						<MarketChart theses={theses} />
					</div>

					{asset && <StatsRail asset={asset} />}
				</div>
			</div>

			{/* The thesis composer and the asset's tables, right under the
			    numbers — the phone gets the same Holders | Swaps | Thesis | My
			    history strip the desktop workspace has, so the social tab was
			    never needed as a separate place to go. */}
			{asset && (
				<React.Fragment>
					<TwetchDoor context={{ id: asset.id, symbol: asset.name || asset.id }} compact />
					<AssetTables
						asset={asset}
						myAddress={myAddress}
						thesisFeed={thesisFeed}
						holdersCount={stats?.holders ?? null}
						className="h-[70dvh]"
					/>
				</React.Fragment>
			)}

			{/* The Buy/Sell bar — the phone's door into the numpad sheet. Sits
			    above the status strip, inside the centered column. */}
			{asset && (
				<div className="fixed inset-x-0 bottom-[calc(1.75rem+env(safe-area-inset-bottom))] z-30 lg:hidden">
					<div className="mx-auto flex w-full max-w-[520px] gap-2.5 px-4 pb-2">
						<button
							type="button"
							data-testid="alkanes-open-buy"
							onClick={() => setSheet('buy')}
							className="h-[52px] flex-1 rounded-2xl bg-primary text-base font-bold text-primary-foreground shadow-lg shadow-primary/30 transition-colors hover:bg-primary/90"
						>
							Buy
						</button>
						<button
							type="button"
							data-testid="alkanes-open-sell"
							onClick={() => setSheet('sell')}
							className="h-[52px] flex-1 rounded-2xl bg-secondary text-base font-bold transition-colors hover:bg-secondary/70"
						>
							Sell
						</button>
					</div>
				</div>
			)}

			{sheet && asset && <TradeSheet side={sheet} onClose={() => setSheet(null)} />}
		</section>
	)
}
